/**
 * The application detail page's code, fetched before it is needed.
 *
 * The page is its own chunk — 156 KB, about 36 KB as served — and a lazy route
 * only starts downloading its chunk when it first renders. On a hard load that
 * is after the session check has finished, and on an in-app visit it is after
 * the click; either way every request the page makes waits behind the
 * download. Cached, that costs nothing. Uncached — the first open after every
 * deploy — it cost 0.17–0.49 s to fetch on its own, and 0.83 s in a hard load
 * on 2026-09-25, all of it in front of the page's first read.
 *
 * So the download starts earlier:
 *
 *   - on a hard load of an application's URL (a notification e-mail links
 *     straight to one), as the app starts, alongside the session check —
 *     App.tsx, as it already does for the admin shell;
 *   - on the pages that list applications, once their rows are on screen and
 *     the browser is idle, so the first click finds it cached.
 *
 * Nothing about the page changes. The route still renders it through
 * React.lazy with the same import, so a failed early download is simply
 * forgotten and the route's own attempt reports failures as it always has.
 */
import { useEffect } from "react";

/** The route's import, shared so the early download and the route are one module. */
export const loadApplicationDetailPage = () => import("./ApplicationDetailPage");

const UUID =
  "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const APPLICATION_DETAIL_PATH = new RegExp(
  `^/admin/applications/${UUID}/?$`,
  "i",
);

/** Whether a path is the detail page's route, `/admin/applications/:id`. */
export function isApplicationDetailPath(pathname: string): boolean {
  return APPLICATION_DETAIL_PATH.test(pathname);
}

/**
 * One early download per page load. Exported for tests; the app uses the
 * instance below.
 */
export function createPreloader(load: () => Promise<unknown>) {
  let pending: Promise<unknown> | null = null;
  return (): void => {
    if (pending) return;
    pending = load().catch(() => {
      // Forget it, so a later call can try again. The route reports its own
      // failure if the page is opened.
      pending = null;
    });
  };
}

/** Starts downloading the detail page's code, once. */
export const preloadApplicationDetailPage = createPreloader(
  loadApplicationDetailPage,
);

/** How long to wait where the browser has no requestIdleCallback. */
export const IDLE_FALLBACK_MS = 1500;

/**
 * Runs `task` when the browser is next idle — after a short delay where
 * requestIdleCallback is missing (Safari) — and returns a cancel function.
 */
export function whenIdle(task: () => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(() => task(), {
      timeout: IDLE_FALLBACK_MS * 2,
    });
    return () => window.cancelIdleCallback?.(handle);
  }
  const handle = window.setTimeout(task, IDLE_FALLBACK_MS);
  return () => window.clearTimeout(handle);
}

/**
 * For pages that link to applications: once `ready` — their rows are on
 * screen — download the detail page's code when the browser is idle.
 */
export function usePreloadApplicationDetailPage(ready: boolean): void {
  useEffect(() => {
    if (!ready) return undefined;
    return whenIdle(preloadApplicationDetailPage);
  }, [ready]);
}
